import { useCallback, useEffect, useState } from 'react';
import { useAssistant } from './context/AssistantContext';
import { useAuth } from './context/AuthContext';
import { stacCountsApi } from './services/api';
import AssistantPanel from './stac/AssistantPanel';
import Header, { type NavSection, type NotifItem } from './stac/Header';
import StacErrorBoundary from './stac/StacErrorBoundary';
import StacShell from './stac/StacShell';
import TabBar, { ClassifyIcon, NexusIcon, ReturnsIcon, type TabItem } from './stac/TabBar';

type PageId = 'nexus' | 'classification' | 'returns' | 'ai-assistant' | 'settings';

interface BadgeCounts {
  nexusAlerts: number;
  unclassified: number;
}

const BADGE_POLL_MS = 5 * 60 * 1000;

export default function App(): JSX.Element {
  const { organizationId, user, logout } = useAuth();
  const { isOpen: assistantOpen, toggle: toggleAssistant } = useAssistant();
  const [activePage, setActivePage] = useState<PageId>('nexus');
  const [badge, setBadge] = useState<BadgeCounts>({ nexusAlerts: 0, unclassified: 0 });
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    function fetchBadgeCounts(): void {
      Promise.allSettled([
        stacCountsApi.nexusAlertsCount(organizationId),
        stacCountsApi.classificationQueueCount(organizationId),
      ]).then(([alerts, queue]) => {
        setBadge({
          nexusAlerts: alerts.status === 'fulfilled' ? (alerts.value.count ?? 0) : 0,
          unclassified: queue.status === 'fulfilled' ? (queue.value.count ?? 0) : 0,
        });
      });
    }
    fetchBadgeCounts();
    const id = setInterval(fetchBadgeCounts, BADGE_POLL_MS);
    return () => clearInterval(id);
  }, [organizationId]);

  const dismissNotif = useCallback((id: string): void => {
    setDismissed((prev) => new Set([...prev, id]));
  }, []);

  const notifications: NotifItem[] = [
    ...(badge.nexusAlerts > 0 && !dismissed.has('nexus')
      ? [
          {
            id: 'nexus',
            type: 'nexus' as const,
            title: `${badge.nexusAlerts} state${badge.nexusAlerts !== 1 ? 's' : ''} triggered nexus`,
            body: 'Registration required to avoid back taxes and penalties.',
            onClick: (): void => setActivePage('nexus'),
          },
        ]
      : []),
    ...(badge.unclassified > 0 && !dismissed.has('unclassified')
      ? [
          {
            id: 'unclassified',
            type: 'unclassified' as const,
            title: `${badge.unclassified} product${badge.unclassified !== 1 ? 's' : ''} need${badge.unclassified === 1 ? 's' : ''} TIC classification`,
            body: 'Tax cannot be calculated for unclassified items until classified.',
            onClick: (): void => setActivePage('classification'),
          },
        ]
      : []),
  ];

  const section: NavSection = activePage === 'ai-assistant' ? 'ai' : 'tax';

  function handleSectionChange(next: NavSection): void {
    if (next === section) return;
    setActivePage(next === 'ai' ? 'ai-assistant' : 'nexus');
  }

  function handleTabSelect(id: string): void {
    if (id === 'nexus' || id === 'classification' || id === 'returns') setActivePage(id);
  }

  async function handleLogout(): Promise<void> {
    // logout(true) would trigger PropelAuth's tenant-wide post-logout redirect,
    // which can send the user to the sibling app sharing this auth tenant.
    // Clear the session without redirecting, then navigate back to STAC's own root.
    await logout(false);
    window.location.href = window.location.origin;
  }

  const tabItems: TabItem[] = [
    { id: 'nexus', label: 'Nexus Status', icon: NexusIcon, badge: badge.nexusAlerts },
    {
      id: 'classification',
      label: 'Products & Classification',
      icon: ClassifyIcon,
      badge: badge.unclassified,
    },
    { id: 'returns', label: 'Tax Returns', icon: ReturnsIcon },
  ];

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: "'Inter', system-ui, sans-serif",
        background: '#F1F5F9',
      }}
    >
      <Header
        section={section}
        onSectionChange={handleSectionChange}
        user={user}
        organizationId={organizationId}
        onSettings={() => setActivePage('settings')}
        onLogout={handleLogout}
        notifications={notifications}
        onDismissNotif={dismissNotif}
        onDismissAll={() => setDismissed(new Set(['nexus', 'unclassified']))}
      />

      {section === 'ai' ? (
        <main style={{ flex: 1, overflow: 'hidden' }}>
          <StacErrorBoundary>
            <StacShell activePage={activePage} organizationId={organizationId} />
          </StacErrorBoundary>
        </main>
      ) : (
        <>
          <TabBar
            items={tabItems}
            activeId={activePage}
            onSelect={handleTabSelect}
            assistantOpen={assistantOpen}
            onToggleAssistant={toggleAssistant}
          />
          <div
            style={{
              flex: 1,
              display: 'flex',
              overflow: 'hidden',
              gap: 16,
              padding: assistantOpen ? 16 : 0,
              boxSizing: 'border-box',
            }}
          >
            {assistantOpen && <AssistantPanel />}
            <main style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
              <StacErrorBoundary>
                <StacShell activePage={activePage} organizationId={organizationId} />
              </StacErrorBoundary>
            </main>
          </div>
        </>
      )}
    </div>
  );
}
